/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 *
 * POC COPY — lean version of
 *   cds-ai-service/frontend/src/views/dev-chat/chat-stream/MessageItem.jsx
 * Intentionally drops dev-chat-only affordances (thumbs feedback, LRR trace
 * tabs, tool-call accordions, citations, entity links, chart/grid panels).
 * Keeps: user bubble, assistant streaming markdown, copy-to-clipboard, thumbs
 * feedback, and follow-up chips.
 * See ./README.md for the extraction plan.
 */

import React, { useMemo, useState } from 'react';

import { getFeedbackButtonSx, getMessageItemStyles } from './MessageItem.styles';
import FeedbackDialog from './FeedbackDialog.jsx';
import { buildFeedbackMessageRef } from './messageRefUtils.js';
import StreamingMessage from './StreamingMessage.jsx';
import CommandIntelligenceGenerationProgress from '../CommandIntelligenceGenerationProgress.jsx';
import { hasGenerationSnapshot } from './lrrProgress';
import Build from '@mui/icons-material/Build';
import ContentCopy from '@mui/icons-material/ContentCopy';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ThumbDown from '@mui/icons-material/ThumbDown';
import ThumbUp from '@mui/icons-material/ThumbUp';
import { Alert, alpha, Box, Button, Chip, IconButton, Stack, Typography, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';
import { usePermissions } from '../../user';

const truncateForChip = (value, max = 60) => {
  if (typeof value !== 'string') {
    return '';
  }

  if (value.length <= max) {
    return value;
  }

  return `${value.slice(0, max - 1)}\u2026`;
};

const summarizeToolInput = input => {
  if (input === null || input === undefined) {
    return '';
  }

  if (typeof input === 'string') {
    return truncateForChip(input);
  }

  if (typeof input !== 'object') {
    return truncateForChip(String(input));
  }

  if (typeof input.sql === 'string' && input.sql.trim() !== '') {
    const oneLine = input.sql.replace(/\s+/g, ' ').trim();

    return `sql: ${truncateForChip(oneLine, 80)}`;
  }

  if (typeof input.question === 'string' && input.question.trim() !== '') {
    return `question: ${truncateForChip(input.question, 60)}`;
  }

  if (Array.isArray(input.tableNames) && input.tableNames.length > 0) {
    return `tables: ${input.tableNames.slice(0, 6).join(', ')}`;
  }

  return truncateForChip(JSON.stringify(input), 80);
};

const RunDetailsCard = ({ metadata, theme }) => {
  const [open, setOpen] = useState(false);

  const usage = metadata?.usage && typeof metadata.usage === 'object' ? metadata.usage : null;
  const model = typeof metadata?.model === 'string' ? metadata.model : '';
  const toolCalls = Array.isArray(metadata?.toolCalls) ? metadata.toolCalls : [];
  const toolsUsed = Array.isArray(metadata?.toolsUsed) ? metadata.toolsUsed : [];

  const inputTokens = typeof usage?.inputTokens === 'number' ? usage.inputTokens : null;
  const outputTokens = typeof usage?.outputTokens === 'number' ? usage.outputTokens : null;
  const toolCount = toolCalls.length || toolsUsed.length || 0;
  const latencyMs =
    typeof metadata?.latencyMs === 'number'
      ? metadata.latencyMs
      : typeof metadata?.serverDurationMs === 'number'
      ? metadata.serverDurationMs
      : null;
  const latencySeconds = latencyMs !== null && latencyMs >= 0 ? latencyMs / 1000 : null;

  if (!model && inputTokens === null && outputTokens === null && toolCount === 0 && latencySeconds === null) {
    return null;
  }

  const summaryParts = [];

  if (toolCount > 0) {
    summaryParts.push(`${toolCount} tool ${toolCount === 1 ? 'call' : 'calls'}`);
  }

  if (inputTokens !== null || outputTokens !== null) {
    summaryParts.push(
      `${inputTokens !== null ? inputTokens.toLocaleString() : '\u2014'} in / ${
        outputTokens !== null ? outputTokens.toLocaleString() : '\u2014'
      } out`
    );
  }

  if (latencySeconds !== null) {
    summaryParts.push(`${latencySeconds.toFixed(2)}s`);
  }

  if (model) {
    summaryParts.push(model);
  }

  return (
    <Box
      sx={{
        mt: 1,
        borderRadius: 1.5,
        border: 1,
        borderColor: 'divider',
        bgcolor: alpha(theme.palette.text.primary, 0.03),
        overflow: 'hidden',
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={() => setOpen(prev => !prev)}
        aria-expanded={open}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.25,
          py: 0.5,
          bgcolor: 'transparent',
          border: 0,
          color: 'text.secondary',
          fontSize: '0.7rem',
          fontWeight: 500,
          textAlign: 'left',
          cursor: 'pointer',
          '&:hover': {
            bgcolor: 'action.hover',
          },
        }}
      >
        <Build sx={{ fontSize: 14, color: 'text.disabled' }} />
        <Box sx={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          Run details {summaryParts.length > 0 ? `\u00b7 ${summaryParts.join(' \u00b7 ')}` : ''}
        </Box>
        {open ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
      </Box>
      {open ? (
        <Box sx={{ px: 1.5, pt: 0.5, pb: 1, borderTop: 1, borderColor: 'divider' }}>
          {model ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              <strong>Model:</strong> {model}
            </Typography>
          ) : null}
          {usage ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
              <strong>Tokens:</strong>{' '}
              {`${inputTokens !== null ? inputTokens.toLocaleString() : '\u2014'} input \u00b7 ${
                outputTokens !== null ? outputTokens.toLocaleString() : '\u2014'
              } output`}
              {typeof usage.totalTokens === 'number' ? ` \u00b7 ${usage.totalTokens.toLocaleString()} total` : ''}
            </Typography>
          ) : null}
          {latencySeconds !== null ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
              <strong>Response time:</strong> {latencySeconds.toFixed(2)}s
            </Typography>
          ) : null}
          {toolCalls.length > 0 ? (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                <strong>Tool calls ({toolCalls.length}):</strong>
              </Typography>
              <Stack spacing={0.5}>
                {toolCalls.map((call, i) => {
                  const name = call?.toolName ?? call?.name ?? 'tool';
                  const summary = summarizeToolInput(call?.input);

                  return (
                    <Box
                      key={`${name}-${i}`}
                      sx={{
                        display: 'flex',
                        gap: 0.75,
                        alignItems: 'flex-start',
                        fontSize: '0.7rem',
                        lineHeight: 1.35,
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          flexShrink: 0,
                          color: 'text.disabled',
                          fontFamily: 'monospace',
                          minWidth: '1.5ch',
                        }}
                      >
                        {i + 1}.
                      </Box>
                      <Chip
                        size="small"
                        label={name}
                        sx={{
                          height: 18,
                          fontSize: '0.65rem',
                          fontFamily: 'monospace',
                          fontWeight: 600,
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: 'primary.main',
                          '& .MuiChip-label': { px: 0.75 },
                        }}
                      />
                      {summary ? (
                        <Box
                          component="span"
                          sx={{
                            color: 'text.secondary',
                            fontFamily: 'monospace',
                            fontSize: '0.68rem',
                            wordBreak: 'break-all',
                          }}
                        >
                          {summary}
                        </Box>
                      ) : null}
                    </Box>
                  );
                })}
              </Stack>
            </>
          ) : toolsUsed.length > 0 ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              <strong>Tools used:</strong> {toolsUsed.join(', ')}
            </Typography>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
};

const MessageItemComponent = ({
  message,
  onSendMessage,
  onSubmitFeedback,
  entityRef,
  chatChannelRef,
  showCopyButton = false,
  markdownDensity = 'compact',
  sendDisabled = false,
}) => {
  const intl = useIntl();
  const theme = useTheme();
  const { isPlatformAdmin, isPlatformSupport } = usePermissions();
  const canViewRunDetails = isPlatformAdmin || isPlatformSupport;
  const styles = useMemo(() => getMessageItemStyles(theme), [theme]);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [feedbackError, setFeedbackError] = useState(null);
  const [pendingRating, setPendingRating] = useState(null);
  const isUser = message.role === 'user';
  const isSystem = message.role === 'system';
  const content = message.text || message.content || '';
  const reasoningText = message.reasoningText || message.metadata?.reasoningText || message.metadata?.reasoning || '';
  const generationSnapshot = message.metadata?.generationSnapshot ?? null;
  const followUpQuestions = message.followUpQuestions || message.metadata?.followUpQuestions || [];
  const hasFollowUpQuestions = Array.isArray(followUpQuestions) && followUpQuestions.length > 0;
  const existingFeedback = message.metadata?.feedback?.rating;
  const activeFeedback = pendingRating ?? existingFeedback;
  const resolvedChannelRef =
    chatChannelRef || (typeof message.chatChannelRef === 'string' ? message.chatChannelRef : '');
  const canShowFeedback =
    !isUser &&
    !isSystem &&
    !message.isStreaming &&
    onSubmitFeedback &&
    entityRef &&
    resolvedChannelRef &&
    !resolvedChannelRef.startsWith('temp-') &&
    Boolean(content);

  const handleCopyToClipboard = () => {
    const raw = message.text || message.content || '';

    if (raw && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(raw);
    }
  };

  const messageStyle = isSystem ? { ...styles.row(false), opacity: 0.85 } : styles.row(isUser);

  return (
    <Box sx={messageStyle}>
      {isUser ? (
        <Box sx={styles.userText}>{content}</Box>
      ) : isSystem ? (
        <Box sx={styles.systemText}>
          <Box component="p" sx={{ m: 0 }}>
            {content}
          </Box>
          {hasGenerationSnapshot(generationSnapshot) ? (
            <Box sx={{ mt: 1 }}>
              <CommandIntelligenceGenerationProgress
                snapshot={generationSnapshot}
                isActive={false}
                defaultExpanded={false}
              />
            </Box>
          ) : null}
        </Box>
      ) : (
        <Box sx={styles.assistantText}>
          <StreamingMessage
            content={content}
            isStreaming={Boolean(message.isStreaming)}
            markdownDensity={markdownDensity}
            reasoningText={reasoningText}
            generationSnapshot={generationSnapshot}
          />
          {!message.isStreaming &&
          Array.isArray(message.metadata?.creditLimitWarnings) &&
          message.metadata.creditLimitWarnings.length > 0 ? (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              {message.metadata.creditLimitWarnings.map((w, i) => (
                <span key={`${w.scope}-${i}`}>
                  {i > 0 ? '; ' : ''}
                  {intl.formatMessage(
                    { id: 'command_intelligence_credit_warning_line' },
                    { pct: w.percentUsed, scope: w.scope }
                  )}
                </span>
              ))}
            </Alert>
          ) : null}
          {!message.isStreaming && canViewRunDetails ? (
            <RunDetailsCard metadata={message.metadata} theme={theme} />
          ) : null}
          {hasFollowUpQuestions && !message.isStreaming ? (
            <Box sx={{ mt: 2 }}>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ display: 'block', letterSpacing: '0.08em', lineHeight: 1, mb: 1 }}
              >
                {intl.formatMessage({ id: 'command_intelligence_suggested_followups' })}
              </Typography>
              <Stack spacing={0.75}>
                {followUpQuestions.map((question, index) => (
                  <Button
                    key={`${question}-${index}`}
                    variant="outlined"
                    size="small"
                    fullWidth
                    onClick={onSendMessage ? () => onSendMessage(question) : undefined}
                    disabled={!onSendMessage || sendDisabled}
                    sx={{
                      justifyContent: 'flex-start',
                      textAlign: 'left',
                      textTransform: 'none',
                      fontWeight: 400,
                      px: 1.5,
                      py: 0.75,
                      lineHeight: 1.4,
                      ...styles.followUpChip,
                    }}
                  >
                    {question}
                  </Button>
                ))}
              </Stack>
            </Box>
          ) : null}
          {(showCopyButton || canShowFeedback) && !message.isStreaming && (message.text || message.content) ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mt: 1 }}>
              {showCopyButton ? (
                <IconButton
                  size="small"
                  onClick={handleCopyToClipboard}
                  sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                  aria-label={intl.formatMessage({ id: 'command_intelligence_copy_aria' })}
                >
                  <ContentCopy sx={{ fontSize: 16 }} />
                </IconButton>
              ) : null}
              {canShowFeedback ? (
                <>
                  <IconButton
                    size="small"
                    onClick={async () => {
                      if (existingFeedback === 'positive' || isSubmittingFeedback) {
                        return;
                      }

                      const metadata = message.metadata && typeof message.metadata === 'object' ? message.metadata : {};

                      setIsSubmittingFeedback(true);
                      setFeedbackError(null);
                      setPendingRating('positive');

                      try {
                        await onSubmitFeedback({
                          chatChannelRef: resolvedChannelRef,
                          messageRef: buildFeedbackMessageRef(message),
                          rating: 'positive',
                          timestamp: Date.now(),
                          runRef: typeof metadata.runRef === 'string' ? metadata.runRef : undefined,
                          agentRef: typeof metadata.agentRef === 'string' ? metadata.agentRef : undefined,
                        });
                      } catch (err) {
                        setPendingRating(null);
                        setFeedbackError(
                          err?.message || intl.formatMessage({ id: 'command_intelligence_feedback_submit_error' })
                        );
                      } finally {
                        setIsSubmittingFeedback(false);
                      }
                    }}
                    disabled={isSubmittingFeedback || activeFeedback === 'positive'}
                    sx={getFeedbackButtonSx(theme, 'positive', activeFeedback)}
                    aria-label={intl.formatMessage({ id: 'command_intelligence_feedback_good_response' })}
                  >
                    <ThumbUp sx={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (existingFeedback === 'negative' || isSubmittingFeedback) {
                        return;
                      }

                      setFeedbackDialogOpen(true);
                    }}
                    disabled={isSubmittingFeedback || existingFeedback === 'negative'}
                    sx={getFeedbackButtonSx(theme, 'negative', activeFeedback)}
                    aria-label={intl.formatMessage({ id: 'command_intelligence_feedback_bad_response' })}
                  >
                    <ThumbDown sx={{ fontSize: 16 }} />
                  </IconButton>
                </>
              ) : null}
            </Box>
          ) : null}
          {feedbackError ? (
            <Alert severity="error" sx={{ mt: 1 }}>
              {feedbackError}
            </Alert>
          ) : null}
          {feedbackDialogOpen ? (
            <FeedbackDialog
              open={feedbackDialogOpen}
              onClose={() => setFeedbackDialogOpen(false)}
              message={message}
              channelId={resolvedChannelRef}
              onSubmit={onSubmitFeedback}
            />
          ) : null}
        </Box>
      )}
    </Box>
  );
};

const areEqual = (prevProps, nextProps) => {
  const prevMessage = prevProps.message;
  const nextMessage = nextProps.message;

  if (prevMessage === nextMessage) {
    return true;
  }

  return (
    prevMessage.id === nextMessage.id &&
    prevMessage.text === nextMessage.text &&
    prevMessage.content === nextMessage.content &&
    prevMessage.isStreaming === nextMessage.isStreaming &&
    prevMessage.role === nextMessage.role &&
    prevMessage.metadata === nextMessage.metadata &&
    prevMessage.followUpQuestions === nextMessage.followUpQuestions &&
    prevProps.onSendMessage === nextProps.onSendMessage &&
    prevProps.onSubmitFeedback === nextProps.onSubmitFeedback &&
    prevProps.entityRef === nextProps.entityRef &&
    prevProps.chatChannelRef === nextProps.chatChannelRef &&
    prevProps.showCopyButton === nextProps.showCopyButton &&
    prevProps.markdownDensity === nextProps.markdownDensity &&
    prevProps.sendDisabled === nextProps.sendDisabled
  );
};

const MessageItem = React.memo(MessageItemComponent, areEqual);

export default MessageItem;
